/**
 * Hit testing — screen tap → object resolution.
 *
 * Strategy: convert the tap to world space (inverse camera), then test
 * objects TOPMOST-FIRST (reverse render order) with an exact per-shape test:
 * rotated-rect for rect-family types, ellipse for circle-family. The shape
 * is resolved via an injectable `resolveShape` so the RendererRegistry can
 * own per-type hit shapes without this module importing the registry
 * (keeps `utils/` dependency-free and unit-testable).
 *
 * @module floorplan/utils/hitTest
 * @category FloorPlan
 */

import type { Vec2 } from '../models/geometry';
import type { FloorObject } from '../models/object';
import { effectiveSize, objectCenter, rotatePoint } from './bounds';

/** Supported hit-test shapes. */
export type HitShape = 'rect' | 'ellipse';

export type ResolveHitShape = (object: FloorObject) => HitShape;

/** Default shape resolution: circle-family types test as ellipses. */
export const defaultResolveHitShape: ResolveHitShape = (object) => {
  if (object.type === 'circle') return 'ellipse';
  return 'rect';
};

/** Exact point-in-rotated-rect test (inverse-rotates the point). */
export const pointInRotatedRect = (
  point: Vec2,
  object: FloorObject,
): boolean => {
  const center = objectCenter(object);
  const local =
    object.rotation === 0
      ? point
      : rotatePoint(point, center, -object.rotation);
  const size = effectiveSize(object);
  if (local.x < object.x) return false;
  if (local.y < object.y) return false;
  if (local.x > object.x + size.width) return false;
  if (local.y > object.y + size.height) return false;
  return true;
};

/** Exact point-in-ellipse test (the ellipse inscribed in the object frame). */
export const pointInEllipse = (point: Vec2, object: FloorObject): boolean => {
  const center = objectCenter(object);
  const local =
    object.rotation === 0
      ? point
      : rotatePoint(point, center, -object.rotation);
  const size = effectiveSize(object);
  const rx = size.width / 2;
  const ry = size.height / 2;
  if (rx <= 0 || ry <= 0) return false;
  const nx = (local.x - center.x) / rx;
  const ny = (local.y - center.y) / ry;
  return nx * nx + ny * ny <= 1;
};

/** Single-object hit test with shape dispatch. */
export const hitTestObject = (
  point: Vec2,
  object: FloorObject,
  resolveShape: ResolveHitShape = defaultResolveHitShape,
): boolean => {
  if (resolveShape(object) === 'ellipse') return pointInEllipse(point, object);
  return pointInRotatedRect(point, object);
};

export type HitTestOptions = {
  /** Skip locked objects (default true — locked objects aren't selectable). */
  skipLocked?: boolean;
  resolveShape?: ResolveHitShape;
};

/**
 * Resolve the TOPMOST object at a world point.
 *
 * `objects` must be in RENDER ORDER (bottom → top); the scan runs from the
 * end so the visually-topmost hit wins. Invisible objects never hit.
 */
export const hitTestTopmost = (
  point: Vec2,
  objects: readonly FloorObject[],
  options?: HitTestOptions,
): FloorObject | null => {
  const skipLocked = options?.skipLocked ?? true;
  const resolveShape = options?.resolveShape ?? defaultResolveHitShape;
  for (let i = objects.length - 1; i >= 0; i -= 1) {
    const object = objects[i];
    if (object === undefined) continue;
    if (!object.visible) continue;
    if (skipLocked && object.locked) continue;
    if (hitTestObject(point, object, resolveShape)) return object;
  }
  return null;
};
